import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { IpFilter } from '@nx/state';
import { ACTION, consoleApi, useActiveOrg, useNotifications, usePermissions } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  allowListDifferent,
  descriptionDifferent,
  entitiesDifferent,
  formHasChanged,
  nameDifferent,
  parseErrorResponse,
} from './helpers';
import { IpFilterSidebar } from './sidebar';
import type { IpFilterData } from './types';
import { IP_FILTERING_ACTION } from './types';

const logger = createLogger(APP_SCOPE.aura);

interface EditIpFilterSidebarProps {
  ipFilter: IpFilter;
  onClose: () => void;
}

export const EditIpFilterSidebar = ({ ipFilter, onClose }: EditIpFilterSidebarProps) => {
  const { addNotification } = useNotifications();

  const { isAllowed } = usePermissions();
  const organization = useActiveOrg();

  const isAllowedToUpdateIpFilters = isAllowed(
    ACTION.UPDATE,
    `organizations/${organization.id}/ip-filters/${ipFilter.id}`,
  );

  const [updateIpFilter] = consoleApi.useUpdateIpFilterMutation();

  // const arraysEqual = (a: string[], b: string[]): boolean => {
  //   if (a.length !== b.length) {
  //     return false;
  //   }

  //   a.sort();
  //   b.sort();
  //   return a.length === b.length && a.every((element, index) => element === b[index]);
  // };

  // const nameDifferent = (data: IpFilterData) => data.name?.trim() !== ipFilter.name;

  // const descriptionDifferent = (data: IpFilterData) => data.description !== ipFilter.description;

  // const allowListDifferent = (data: IpFilterData) =>
  //   !arraysEqual(
  //     data.allowList ? data.allowList.map((ip) => `${ip.IpRange}/${ip.Description}`) : [],
  //     ipFilter.allowList.map((ip) => `${ip.address}/${ip.prefixLen}/${ip.description}`),
  //   );

  // // Check entities are different
  // const entitiesDifferent = (data: IpFilterData) =>
  //   !arraysEqual(data.filteredEntities?.instances ?? [], ipFilter.filteredEntities?.instances ?? []) ||
  //   !arraysEqual(data.filteredEntities?.projects ?? [], ipFilter.filteredEntities?.projects ?? []) ||
  //   !arraysEqual(data.filteredEntities?.organizations ?? [], ipFilter.filteredEntities?.organizations ?? []);

  const buildRequest = (data: IpFilterData) => {
    const nameHasChanged = nameDifferent(data, ipFilter);
    const descriptionHasChanged = descriptionDifferent(data, ipFilter);
    const allowListHasChanged = allowListDifferent(data, ipFilter);
    const entitiesHaveChanged = entitiesDifferent(data, ipFilter);
    if (formHasChanged(data, ipFilter)) {
      return {
        id: ipFilter.id,
        Name: nameHasChanged ? data.name?.trim() : undefined,
        Description: descriptionHasChanged ? data.description?.trim() : undefined,
        AllowList: allowListHasChanged ? data.allowList : undefined,
        Entities: entitiesHaveChanged ? data.filteredEntities : undefined,
      };
    }
    return null;
  };

  const update = (data: IpFilterData): void => {
    const requestBody = buildRequest(data);

    if (isNullish(requestBody)) {
      addNotification({
        type: 'info',
        title: 'No changes detected',
        description: 'No changes were made to the IP Filter.',
        timeout: 5000,
      });
      return;
    }

    updateIpFilter(requestBody)
      .unwrap()
      .then((response) => {
        onClose();
      })
      .catch((error: FetchBaseQueryError | SerializedError | undefined) => {
        const errorMessage = parseErrorResponse(error);
        logger.error(errorMessage);

        addNotification({
          type: 'danger',
          title: 'Failed to create IP Filter',
          description: errorMessage,
          timeout: 5000,
        });
      });
  };

  return (
    <IpFilterSidebar
      onClose={onClose}
      action={IP_FILTERING_ACTION.UPDATE}
      hasActionPermission={isAllowedToUpdateIpFilters}
      onSubmit={update}
      existingIpFilter={ipFilter}
    />
  );
};
